import * as React from 'react'
import { closeWidgetModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import ModalLoader from './Parts/ModalLoader'
import Button from '../Button/Button'
import { WidgetsController } from '../../controllers'
import { CurrentUser, DisplayableError, Widget } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import CheckboxInput from '../Form/CheckboxInput'

export type WidgetModalTab = 'details' | 'notes' | 'planning'

interface IStateToProps {
  currentUser: CurrentUser
  widget?: Widget
  onSubmit: (widget: Widget) => void
}

interface IDispatchToProps {
  close: typeof closeWidgetModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  didInitialLoad: boolean
  widget: Widget
  errors: DisplayableError[]
}

const WidgetModal = (props: IProps) => {
  const { t } = useTranslation()
  const { currentUser } = props
  const [state, setState] = React.useState<IState>({
    didInitialLoad: false,
    widget: null,
    errors: [],
  })
  const { widget, errors, didInitialLoad } = state

  React.useEffect(() => {
    getForm().catch(console.error)
  }, [])

  const getForm = async () => {
    try {
      const { widget } = await WidgetsController.getForm({ id: props.widget ? props.widget.id : null })

      setState({
        ...state,
        widget: {
          ...widget,
          ...props.widget,
        },
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }


  const onNameChange = (e) => {
    setState({ ...state, widget: { ...widget, name: e.currentTarget.value } })
  }

  const onEnabledChange = () => {
    setState({ ...state, widget: { ...widget, enabled: !widget.enabled } })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onFormSubmit = async (e) => {
    e.preventDefault()

    try {
      let response = null
      if (widget.id) {
        response = await WidgetsController.update(widget)
      } else {
        response = await WidgetsController.create(widget)
      }

      if (response.errors) {
        setState({ ...state, errors: response.errors })
      } else {
        if (props.onSubmit) props.onSubmit(response)
        props.close()
      }

    } catch (ex) {
      console.error(ex)
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={widget?.id ? t('WidgetModal::Update widget') : t('WidgetModal::Create widget')}
        onCloseClick={onCloseClick}
      />

      {!didInitialLoad && <ModalLoader />}
      {didInitialLoad && <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='grid'>
              <div className='grid-cell with-6col'>
                <div className='form-item'>
                  <label>{t('WidgetModal::Name')} <span>*</span></label>
                  <input
                    type='text'
                    placeholder={t('WidgetModal::Name')}
                    onChange={onNameChange}
                    value={widget.name}
                  />
                </div>
              </div>

              <div className='grid-cell with-6col'>
                <div className='form-item'>
                  <label>{t('WidgetModal::Enabled')}</label>
                  <CheckboxInput
                    checked={widget.enabled}
                    label={t('WidgetModal::Enabled')}
                    onChange={onEnabledChange}
                  />
                </div>
              </div>
            </div>
            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={widget?.id ? t('WidgetModal::Update') : t('WidgetModal::Create')}
              onClick={onFormSubmit}
            />
          </div>
        </div>
      </ModalMiddle>}
    </ModalWindow >
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser
    },
    modals: {
      widgetModal: {
        widget,
        onSubmit,
      }
    }
  } = state

  return {
    currentUser: currentUser,
    widget: widget,
    onSubmit: onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeWidgetModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WidgetModal)